import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import type { IconType } from "react-icons";
import {
  FiAlertOctagon, FiAlertTriangle, FiCheckCircle, FiChevronDown, FiChevronRight, FiClock, FiCornerDownRight,
  FiCrosshair, FiExternalLink, FiHelpCircle, FiSearch, FiShare2, FiX,
} from "react-icons/fi";

import { api, getOverview, OVERVIEW_REFRESH_MS } from "@/lib/api";
import { useSignalBands } from "@/lib/bands";
import { fmtDateTime, fmtNum, useI18n, type Key } from "@/lib/i18n";
import { integrationHealth, kindOf } from "@/lib/integrations";
import { causeKind } from "@/lib/ontstate";
import { rxLevel } from "@/lib/olthealth";
import { fmtDbm } from "@/lib/signal";
import {
  buildTree, discoveryCounts, pathTo, platformName, problemsOnly, refKey, search, worst,
  type DeviceNode, type NodeRef, type NodeState, type OLTNode, type ONTNode, type OntHit, type PONNode,
  type SiteNode, type TopoLink, type TopologyResponse, type TopoUnresolved, type Unresolved,
} from "@/lib/topology";
import { useIntegrations, useNow } from "@/components/MonitoringBanner";
import { OnlineBar } from "@/components/OltStatus";
import { agoText, causeLabel, durText, LiveBadge, RxTag } from "@/components/OntStatus";
import { cn } from "@/lib/utils";

// ---------- vocabulary ----------

const STATE_UI: Record<NodeState, { icon: IconType; text: string; bg: string; dot: string }> = {
  down: { icon: FiAlertOctagon, text: "text-white", bg: "bg-signal-crit", dot: "bg-signal-crit" },
  critical: { icon: FiAlertOctagon, text: "text-signal-crit", bg: "bg-signal-crit/15", dot: "bg-signal-crit" },
  degraded: { icon: FiAlertTriangle, text: "text-signal-warn", bg: "bg-amber-400/10", dot: "bg-signal-warn" },
  stale: { icon: FiClock, text: "text-amber-200", bg: "bg-amber-400/10", dot: "bg-amber-300" },
  warn: { icon: FiAlertTriangle, text: "text-amber-200/90", bg: "bg-amber-400/[0.07]", dot: "bg-amber-300/80" },
  unknown: { icon: FiHelpCircle, text: "text-slate-300", bg: "bg-slate-700/40", dot: "bg-slate-500" },
  healthy: { icon: FiCheckCircle, text: "text-signal-good", bg: "bg-signal-good/10", dot: "bg-signal-good" },
};

function Badge({ state, quiet }: { state: NodeState; quiet?: boolean }) {
  const { t } = useI18n();
  const s = STATE_UI[state];
  // Healthy rows stay quiet: a dot, not a label, so the eye goes to problems.
  if (quiet && state === "healthy") return <span className={cn("inline-block size-1.5 shrink-0 rounded-full", s.dot)} title={t("topo.state.healthy")} />;
  return (
    <span className={cn("inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-sm px-1.5 py-px text-[10px] font-semibold tracking-[0.12em] uppercase", s.text, s.bg)}>
      <s.icon className="size-3" /> {t(`topo.state.${state}` as Key)}
    </span>
  );
}

const CONF_UI: Record<TopoLink["confidence"], { glyph: string; text: string }> = {
  confirmed: { glyph: "●", text: "text-signal-good" },
  discovered: { glyph: "◌", text: "text-slate-300" },
  manual: { glyph: "◇", text: "text-signal" },
};

function ConfBadge({ c }: { c: TopoLink["confidence"] }) {
  const { t } = useI18n();
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.1em] uppercase", CONF_UI[c].text)} title={t(`topo.conf.${c}.hint` as Key)}>
      <span aria-hidden>{CONF_UI[c].glyph}</span>{t(`topo.conf.${c}` as Key)}
    </span>
  );
}

const Mono = ({ children, className }: { children: ReactNode; className?: string }) => <span className={cn("data", className)}>{children}</span>;

// ---------- page ----------

type View = "infra" | "discovery" | "unresolved";

export default function TopologyPage() {
  const { t, locale } = useI18n();
  const [sp, setSp] = useSearchParams();
  const view = (["infra", "discovery", "unresolved"].includes(sp.get("view") ?? "") ? sp.get("view") : "infra") as View;
  const problems = sp.get("f") === "problems";
  const setParam = (k: string, v: string | null) => { const p = new URLSearchParams(sp); if (v === null) p.delete(k); else p.set(k, v); setSp(p, { replace: true }); };

  const now = useNow(5000);
  const bands = useSignalBands();
  const topo = useQuery({ queryKey: ["topology"], queryFn: () => api<TopologyResponse>("/api/v1/topology"), refetchInterval: 60_000 });
  const ov = useQuery({ queryKey: ["overview"], queryFn: getOverview, refetchInterval: OVERVIEW_REFRESH_MS });
  const ints = useIntegrations();

  const tree = useMemo(() => (topo.data && ov.data ? buildTree(topo.data, ov.data, bands, now) : []), [topo.data, ov.data, bands, now]);
  const shown = useMemo(() => (problems ? problemsOnly(tree) : tree), [tree, problems]);
  const allOlts = tree.flatMap((s) => s.olts);
  const issues = tree.flatMap((s) => [...s.olts.filter((o) => o.state !== "healthy"), ...s.devices.filter((d) => d.state !== "healthy")]).length;

  // Expansion: sites open; OLTs open while the fleet is small; PONs closed.
  const [open, setOpen] = useState<Set<string> | null>(null);
  const opened = useMemo(() => {
    if (open) return open;
    const s = new Set<string>();
    for (const site of tree) {
      if (tree.length <= 3 || site.state !== "healthy") s.add(`site:${site.name}`);
      if (allOlts.length <= 10) for (const o of site.olts) s.add(`olt:${o.id}`);
      if (site.devices.length <= 8) s.add(`devs:${site.name}`);
    }
    return s;
  }, [open, tree, allOlts.length]);
  // Problems-only opens the path down to every problem.
  const isOpen = (k: string) => problems || opened.has(k);
  const toggle = (k: string) => { const s = new Set(opened); if (s.has(k)) s.delete(k); else s.add(k); setOpen(s); };

  const [sel, setSel] = useState<NodeRef | null>(null);
  const [pinned, setPinned] = useState<OntHit | null>(null);
  const locate = (ref: NodeRef, hit?: OntHit) => {
    const s = new Set(opened);
    for (const k of pathTo(tree, ref)) s.add(k);
    setOpen(s);
    setSel(ref);
    setPinned(hit ?? null);
    if (view !== "infra" || problems) { const p = new URLSearchParams(sp); p.delete("view"); p.delete("f"); setSp(p, { replace: true }); }
    requestAnimationFrame(() => document.querySelector(`[data-node="${refKey(ref)}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" }));
  };

  // Arriving from the Link Map: ?locate=<map key> ("librenms:680", "olt:1").
  const located = useRef(false);
  useEffect(() => {
    const key = sp.get("locate");
    if (!key || located.current || !topo.data || !tree.length) return;
    located.current = true;
    const olt = topo.data.olts.find((o) => o.map_key === key || `olt:${o.id}` === key);
    const dev = topo.data.devices.find((d) => d.key === key);
    if (olt) locate({ kind: "olt", id: olt.id });
    else if (dev?.olt_id) locate({ kind: "olt", id: dev.olt_id });
    else if (dev) locate({ kind: "device", id: dev.id });
    const p = new URLSearchParams(sp); p.delete("locate"); setSp(p, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topo.data, tree.length]);

  const intRow = (k: string) => ints.data?.integrations.find((r) => kindOf(r.name) === k);
  const ftth = intRow("olt"), disc = intRow("librenms");
  const ftthH = ftth && integrationHealth(ftth, now), discH = disc && integrationHealth(disc, now);
  const discStale = !!discH && (discH.state === "stale" || discH.state === "failed");
  const dc = topo.data ? discoveryCounts(topo.data) : null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-100">{t("nav.topology")}</h1>
          <p className="mt-1 text-[12.5px] text-slate-400">{t("topo.subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[12px]">
          <Fresh label={t("topo.fresh.ftth")} h={ftthH} okAt={ftth?.last_ok_at} now={now} />
          <Fresh label={t("topo.fresh.discovery")} h={discH} okAt={disc?.last_ok_at} now={now} />
        </div>
      </div>

      {topo.error && <p className="text-signal-crit">{(topo.error as Error).message}</p>}
      {(topo.isLoading || ov.isLoading) && <p className="text-slate-400">{t("common.loading")}</p>}

      {topo.data && ov.data && dc && (
        <>
          <SummaryStrip tree={tree} dc={dc} issues={issues} />

          <div className="flex flex-wrap items-center gap-2">
            <div role="tablist" className="flex rounded-sm border border-rule p-0.5">
              {(["infra", "discovery", "unresolved"] as View[]).map((v) => (
                <button key={v} role="tab" type="button" aria-selected={view === v} onClick={() => setParam("view", v === "infra" ? null : v)}
                  className={cn("rounded-sm px-3 py-1 text-[12.5px]", view === v ? "bg-signal/10 text-slate-100" : "text-slate-400 hover:text-slate-100")}>
                  {t(`topo.view.${v}` as Key)}
                  {v === "unresolved" && dc.unresolved > 0 && <span className="data ml-1.5 text-amber-300">{dc.unresolved}</span>}
                </button>
              ))}
            </div>
            {view === "infra" && (
              <div className="flex rounded-sm border border-rule p-0.5">
                {([false, true] as const).map((pr) => (
                  <button key={String(pr)} type="button" aria-pressed={problems === pr} onClick={() => setParam("f", pr ? "problems" : null)}
                    className={cn("rounded-sm px-3 py-1 text-[12.5px]", problems === pr ? "bg-signal/10 text-slate-100" : "text-slate-400 hover:text-slate-100")}>
                    {t(pr ? "topo.filter.problems" : "topo.filter.all")}
                    {pr && <span className={cn("data ml-1.5", issues ? "text-signal-warn" : "text-slate-500")}>{issues}</span>}
                  </button>
                ))}
              </div>
            )}
            <SearchBox tree={tree} onLocate={locate} />
          </div>

          {view === "infra" && (
            <div className="grid items-start gap-3 xl:grid-cols-[minmax(0,1fr)_320px] 2xl:grid-cols-[minmax(0,1fr)_400px]">
              <div className="min-w-0 rounded-lg border border-rule bg-panel py-1.5">
                {shown.length === 0 && <p className="px-4 py-8 text-center text-[13px] text-slate-400">{t(problems ? "topo.noProblems" : "topo.empty")}</p>}
                {shown.map((s) => (
                  <SiteBranch key={s.name} site={s} isOpen={isOpen} toggle={toggle} sel={sel} onSel={(r) => { setSel(r); setPinned(null); }}
                    pinned={pinned} problems={problems} now={now} />
                ))}
              </div>
              <DetailPanel tree={tree} sel={sel} pinned={pinned} topo={topo.data} now={now} onLocate={locate} discStale={discStale} />
            </div>
          )}
          {view === "discovery" && <DiscoveryView topo={topo.data} dc={dc} disc={disc?.last_ok_at ?? null} stale={discStale} now={now} onLocate={locate} locale={locale} />}
          {view === "unresolved" && <UnresolvedView topo={topo.data} stale={discStale} onLocate={locate} />}
        </>
      )}
    </div>
  );
}

function Fresh({ label, h, okAt, now }: { label: string; h: ReturnType<typeof integrationHealth> | undefined; okAt: string | null | undefined; now: number }) {
  const { t, locale } = useI18n();
  const bad = h && (h.state === "stale" || h.state === "failed");
  const off = !h || h.state === "disabled" || h.state === "unknown";
  return (
    <Link to="/integrations" title={okAt ? fmtDateTime(locale, okAt) : undefined}
      className={cn("inline-flex items-center gap-1.5 rounded-sm border px-2 py-1",
        bad ? "border-amber-400/50 bg-amber-400/10 text-amber-100" : "border-rule text-slate-300 hover:border-signal/40")}>
      <span className={cn("size-1.5 rounded-full", bad ? "bg-signal-warn" : off ? "bg-slate-500" : "bg-signal-good")} />
      <span className="text-slate-400">{label}</span>
      <span className="font-semibold tracking-[0.08em] uppercase">{t(bad ? "topo.fresh.stale" : off ? "topo.fresh.none" : "topo.fresh.current")}</span>
      {okAt && <Mono className="text-slate-400">{durText(now - Date.parse(okAt))}</Mono>}
    </Link>
  );
}

function SummaryStrip({ tree, dc, issues }: { tree: SiteNode[]; dc: ReturnType<typeof discoveryCounts>; issues: number }) {
  const { t, locale } = useI18n();
  const n = (v: number) => fmtNum(locale, v);
  const olts = tree.flatMap((s) => s.olts);
  const total = tree.reduce((a, s) => a + s.total, 0), online = tree.reduce((a, s) => a + s.online, 0);
  const cells: [string, ReactNode, boolean?][] = [
    [t("topo.sum.sites"), n(tree.filter((s) => s.name).length)],
    [t("topo.sum.olts"), n(olts.length)],
    [t("topo.sum.pons"), n(tree.reduce((a, s) => a + s.pons, 0))],
    [t("topo.sum.onts"), <>{n(online)}<span className="text-slate-500">/{n(total)}</span></>, online < total],
    [t("topo.sum.devices"), n(tree.reduce((a, s) => a + s.devices.length, 0))],
    [t("topo.sum.links"), <>{n(dc.links)}<span className="ml-1 text-[11px] text-slate-500">{t("topo.sum.confirmedN", { n: n(dc.confirmed) })}</span></>],
    [t("topo.sum.unresolved"), n(dc.unresolved), dc.unresolved > 0],
  ];
  const state = worst(tree.map((s) => s.state));
  return (
    <div className="flex flex-wrap items-stretch overflow-hidden rounded-lg border border-rule bg-panel">
      <div className={cn("flex items-center gap-2 border-r border-rule px-4 py-2.5 text-[12.5px] font-semibold tracking-[0.12em] uppercase", STATE_UI[state].text.replace("text-white", "text-signal-crit"))}>
        {(() => { const I = STATE_UI[state].icon; return <I className="size-4" />; })()}
        {issues ? t("topo.issues", { n: n(issues) }) : t("topo.allHealthy")}
      </div>
      {cells.map(([label, value, warn]) => (
        <div key={label} className="border-r border-rule/70 px-4 py-2 last:border-r-0">
          <div className="eyebrow text-[10px]">{label}</div>
          <div className={cn("data text-[17px] leading-tight", warn ? "text-signal-warn" : "text-slate-100")}>{value}</div>
        </div>
      ))}
    </div>
  );
}

// ---------- tree ----------

type Sel = { sel: NodeRef | null; onSel: (r: NodeRef) => void };

function Row({ depth, open, onToggle, selected, nodeKey, onClick, children, tone }: {
  depth: number; open?: boolean; onToggle?: () => void; selected: boolean; nodeKey: string; onClick: () => void; children: ReactNode; tone?: NodeState;
}) {
  const { t } = useI18n();
  return (
    <div data-node={nodeKey} onClick={onClick}
      className={cn("group relative flex min-h-9 cursor-pointer items-center gap-2 pr-4 text-[13px] hover:bg-white/[0.025]",
        selected && "bg-signal/[0.07] shadow-[inset_2px_0_0_var(--color-signal)]",
        tone && (tone === "down" || tone === "critical") && !selected && "bg-signal-crit/[0.04]")}
      style={{ paddingLeft: 12 + depth * 22 }}>
      {/* Guide lines: one rail per ancestor, the hierarchy stays legible when deep. */}
      {Array.from({ length: depth }, (_, i) => (
        <span key={i} aria-hidden className="absolute top-0 bottom-0 w-px bg-rule" style={{ left: 20 + i * 22 }} />
      ))}
      {onToggle ? (
        <button type="button" aria-expanded={open} aria-label={t(open ? "topo.collapse" : "topo.expand")} onClick={(e) => { e.stopPropagation(); onToggle(); }}
          className="z-10 -ml-1 grid size-5 shrink-0 place-items-center rounded-sm text-slate-500 hover:bg-white/5 hover:text-slate-100">
          {open ? <FiChevronDown /> : <FiChevronRight />}
        </button>
      ) : <span className="w-4 shrink-0" />}
      {children}
    </div>
  );
}

function SiteBranch({ site, isOpen, toggle, sel, onSel, pinned, problems, now }: Sel & {
  site: SiteNode; isOpen: (k: string) => boolean; toggle: (k: string) => void; pinned: OntHit | null; problems: boolean; now: number;
}) {
  const { t, locale } = useI18n();
  const k = `site:${site.name}`;
  const open = isOpen(k);
  const n = (v: number) => fmtNum(locale, v);
  const ref: NodeRef = { kind: "site", site: site.name };
  return (
    <section className="border-b border-rule/60 last:border-b-0">
      <Row depth={0} open={open} onToggle={() => toggle(k)} selected={sel?.kind === "site" && sel.site === site.name} nodeKey={refKey(ref)} onClick={() => onSel(ref)}>
        <span className={cn("text-[13.5px] font-semibold tracking-[0.1em] uppercase", site.name ? "text-slate-100" : "text-slate-400")}>
          {site.name || t("topo.unassigned")}
        </span>
        {site.name && <Badge state={site.state} />}
        <span className="data ml-auto truncate pl-3 text-[12px] text-slate-400">
          {[site.oltCount ? t("topo.nOlt", { n: n(site.oltCount) }) : "", site.pons ? t("topo.nPon", { n: n(site.pons) }) : "",
            site.total ? t("topo.onlineOf", { on: n(site.online), total: n(site.total) }) : "", site.deviceCount ? t("topo.nDev", { n: n(site.deviceCount) }) : ""]
            .filter(Boolean).join("  ·  ")}
        </span>
      </Row>
      {open && (
        <div className="pb-1">
          {!site.name && <p className="py-1 pr-4 text-[12px] text-slate-500" style={{ paddingLeft: 56 }}>{t("topo.unassignedHint")}</p>}
          {site.olts.length > 0 && <Group depth={1} label={t("topo.access")} />}
          {site.olts.map((o) => <OltBranch key={o.id} olt={o} isOpen={isOpen} toggle={toggle} sel={sel} onSel={onSel} pinned={pinned} problems={problems} now={now} />)}
          {site.devices.length > 0 && (
            <DeviceGroup site={site} open={isOpen(`devs:${site.name}`)} toggle={() => toggle(`devs:${site.name}`)} sel={sel} onSel={onSel} />
          )}
        </div>
      )}
    </section>
  );
}

const Group = ({ depth, label, right }: { depth: number; label: string; right?: ReactNode }) => (
  <div className="relative flex items-center gap-2 pt-2 pb-1 pr-4" style={{ paddingLeft: 12 + depth * 22 + 22 }}>
    {Array.from({ length: depth }, (_, i) => <span key={i} aria-hidden className="absolute top-0 bottom-0 w-px bg-rule" style={{ left: 20 + i * 22 }} />)}
    <span className="eyebrow text-[10px]">{label}</span>{right}
  </div>
);

function OltBranch({ olt, isOpen, toggle, sel, onSel, pinned, problems, now }: Sel & {
  olt: OLTNode; isOpen: (k: string) => boolean; toggle: (k: string) => void; pinned: OntHit | null; problems: boolean; now: number;
}) {
  const { t, locale } = useI18n();
  const k = `olt:${olt.id}`;
  const open = isOpen(k);
  const n = (v: number) => fmtNum(locale, v);
  const offline = olt.total - olt.online;
  const ref: NodeRef = { kind: "olt", id: olt.id };
  return (
    <>
      <Row depth={1} open={open} onToggle={() => toggle(k)} selected={sel?.kind === "olt" && sel.id === olt.id} nodeKey={refKey(ref)} onClick={() => onSel(ref)} tone={olt.state}>
        <span className="min-w-0 truncate font-medium whitespace-nowrap text-slate-100" title={olt.name}>{olt.name}</span>
        <Badge state={olt.state} quiet />
        <Mono className="hidden text-[11.5px] text-slate-500 2xl:inline">{olt.ip}</Mono>
        <span className="ml-auto flex shrink-0 items-center gap-3 pl-3">
          <span className="data text-[12px] text-slate-400">{t("topo.nPon", { n: n(olt.pons.length) })}</span>
          <span className="data w-24 text-right text-[12px] text-slate-200">{n(olt.online)}<span className="text-slate-500">/{n(olt.total)}</span></span>
          <span className="w-20"><OnlineBar online={olt.online} total={olt.total} /></span>
          <span className={cn("data w-20 text-right text-[12px]", offline ? "text-signal-crit" : "text-slate-600")}>{offline ? t("topo.nOffline", { n: n(offline) }) : "—"}</span>
        </span>
      </Row>
      {open && olt.pons.map((p) => <PonBranch key={p.id} pon={p} olt={olt} isOpen={isOpen} toggle={toggle} sel={sel} onSel={onSel} pinned={pinned} problems={problems} now={now} />)}
      {open && olt.pons.length === 0 && <p className="py-1 text-[12px] text-slate-500" style={{ paddingLeft: 12 + 2 * 22 + 22 }}>{t("topo.noPons")}</p>}
    </>
  );
}

function PonBranch({ pon, olt, isOpen, toggle, sel, onSel, pinned, problems, now }: Sel & {
  pon: PONNode; olt: OLTNode; isOpen: (k: string) => boolean; toggle: (k: string) => void; pinned: OntHit | null; problems: boolean; now: number;
}) {
  const { t, locale } = useI18n();
  const k = `pon:${pon.id}`;
  const n = (v: number) => fmtNum(locale, v);
  const expandable = pon.total > 0;
  const open = expandable && (isOpen(k) || (problems && pon.problems.length > 0));
  const offline = pon.total - pon.online;
  const ref: NodeRef = { kind: "pon", id: pon.id };
  const pin = pinned && sel?.kind === "ont" && sel.ponId === pon.id && !pon.problems.some((x) => x.id === pinned.id) ? pinned : null;
  const healthy = pon.total - pon.problems.length - (pin ? 1 : 0);
  return (
    <>
      <Row depth={2} open={open} onToggle={expandable ? () => toggle(k) : undefined} selected={sel?.kind === "pon" && sel.id === pon.id} nodeKey={refKey(ref)} onClick={() => onSel(ref)} tone={pon.state}>
        <span className="font-medium whitespace-nowrap text-slate-200">PON {pon.path}</span>
        <Mono className="hidden text-[11px] text-slate-600 2xl:inline">{pon.native}</Mono>
        <Badge state={pon.state} quiet />
        <span className="ml-auto flex shrink-0 items-center gap-3 pl-3">
          <span className={cn("text-[12px]", pon.weak ? "text-amber-200/90" : "text-slate-600")}>{pon.weak ? t("topo.nWeak", { n: n(pon.weak) }) : ""}</span>
          <span className="data w-24 text-right text-[12px] text-slate-300">{n(pon.online)}<span className="text-slate-500">/{n(pon.total)}</span></span>
          <span className="w-20"><OnlineBar online={pon.online} total={pon.total} /></span>
          <span className={cn("data w-20 text-right text-[12px]", offline ? "text-signal-crit" : "text-slate-600")}>{offline ? t("topo.nOffline", { n: n(offline) }) : "—"}</span>
        </span>
      </Row>
      {open && (
        <>
          {pon.problems.map((x) => <OntRow key={x.id} ont={x} pon={pon} sel={sel} onSel={onSel} now={now} />)}
          {pin && (
            <Row depth={3} selected nodeKey={refKey({ kind: "ont", id: pin.id, ponId: pon.id })} onClick={() => {}}>
              <FiCrosshair className="size-3.5 text-signal" />
              <span className="text-slate-100">{pin.description || pin.serial}</span>
              <Mono className="text-[11.5px] text-slate-500">ONT #{pin.ont_id} · {pin.serial}</Mono>
              <span className="ml-auto"><LiveBadge live={pin.run_state === "Online" ? "online" : "offline"} /></span>
            </Row>
          )}
          <div className="relative flex items-center gap-3 py-1.5 pr-4 text-[12px] text-slate-500" style={{ paddingLeft: 12 + 3 * 22 + 22 }}>
            {Array.from({ length: 3 }, (_, i) => <span key={i} aria-hidden className="absolute top-0 bottom-0 w-px bg-rule" style={{ left: 20 + i * 22 }} />)}
            <span className="inline-flex items-center gap-1.5"><span className="size-1.5 rounded-full bg-signal-good/70" />{t("topo.othersHealthy", { n: n(Math.max(0, healthy)) })}</span>
            <Link to={`/olts/${olt.id}?pon=${encodeURIComponent(pon.path)}`} className="ml-auto inline-flex items-center gap-1 text-slate-400 hover:text-signal">
              {t("topo.openPon")} <FiChevronRight />
            </Link>
          </div>
        </>
      )}
    </>
  );
}

function OntRow({ ont, pon, sel, onSel, now }: Sel & { ont: ONTNode; pon: PONNode; now: number }) {
  const { t } = useI18n();
  const bands = useSignalBands();
  const ref: NodeRef = { kind: "ont", id: ont.id, ponId: pon.id };
  const off = ont.problem.kind === "offline" || ont.problem.kind === "missing";
  return (
    <Row depth={3} selected={sel?.kind === "ont" && sel.id === ont.id} nodeKey={refKey(ref)} onClick={() => onSel(ref)} tone={off ? "critical" : "warn"}>
      <span className="w-[5.5rem] shrink-0">
        {off ? <LiveBadge live={ont.problem.kind === "missing" ? "missing" : "offline"} /> : <RxTag level={rxLevel(ont.rx_power_dbm, bands)} />}
      </span>
      <span className="truncate text-slate-100">{ont.description || ont.serial}</span>
      <Mono className="hidden text-[11.5px] text-slate-500 md:inline">ONT #{ont.ont_id}</Mono>
      <span className="data ml-auto shrink-0 pl-3 text-[12px] text-slate-300">
        {off ? <>{causeLabel(causeKind(ont.last_down_cause), t)} <span className="text-slate-500">· {agoText(ont.last_down_at, now)}</span></> : fmtDbm(ont.rx_power_dbm)}
      </span>
    </Row>
  );
}

function DeviceGroup({ site, open, toggle, sel, onSel }: Sel & { site: SiteNode; open: boolean; toggle: () => void }) {
  const { t, locale } = useI18n();
  const down = site.devices.filter((d) => d.state !== "healthy").length;
  return (
    <>
      <div className="relative flex cursor-pointer items-center gap-2 pt-2 pb-1 pr-4" style={{ paddingLeft: 12 + 22 }} onClick={toggle}>
        <span aria-hidden className="absolute top-0 bottom-0 w-px bg-rule" style={{ left: 20 }} />
        <span className="grid size-5 place-items-center text-slate-500">{open ? <FiChevronDown /> : <FiChevronRight />}</span>
        <span className="eyebrow text-[10px]">{t("topo.infra")}</span>
        <span className="data text-[11px] text-slate-500">{fmtNum(locale, site.devices.length)}</span>
        {down > 0 && <span className="text-[11.5px] text-signal-warn">{t("topo.nDown", { n: down })}</span>}
      </div>
      {open && site.devices.map((d) => <DeviceRow key={d.id} d={d} sel={sel} onSel={onSel} />)}
    </>
  );
}

function DeviceRow({ d, sel, onSel }: Sel & { d: DeviceNode }) {
  const { t } = useI18n();
  const ref: NodeRef = { kind: "device", id: d.id };
  return (
    <Row depth={2} selected={sel?.kind === "device" && sel.id === d.id} nodeKey={refKey(ref)} onClick={() => onSel(ref)} tone={d.state}>
      <span className="truncate font-medium text-slate-100">{d.name}</span>
      <Badge state={d.state} quiet />
      <Mono className="text-[11.5px] text-slate-500">{d.hostname}</Mono>
      <span className="ml-auto flex shrink-0 items-center gap-3 pl-3 text-[12px] text-slate-400">
        <span className="hidden truncate lg:inline">{[platformName(d.os), d.hardware].filter(Boolean).join(" · ")}</span>
        <span className="data w-20 text-right">{d.links ? t("topo.nLinks", { n: d.links }) : <span className="text-slate-600">{t("topo.noLinks")}</span>}</span>
      </span>
    </Row>
  );
}

// ---------- search ----------

function SearchBox({ tree, onLocate }: { tree: SiteNode[]; onLocate: (r: NodeRef, hit?: OntHit) => void }) {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [dq, setDq] = useState("");
  useEffect(() => { const id = setTimeout(() => setDq(q.trim()), 250); return () => clearTimeout(id); }, [q]);
  // Healthy ONTs are not in the tree payload: ask the ONT list for them.
  const srv = useQuery({
    queryKey: ["topo-ont-search", dq],
    queryFn: () => api<{ onts: OntHit[] }>(`/api/v1/onts?q=${encodeURIComponent(dq)}&limit=10`),
    enabled: dq.length >= 3,
    staleTime: 30_000,
  });
  const hits = useMemo(() => search(tree, q, dq.length >= 3 ? srv.data?.onts ?? [] : []), [tree, q, dq, srv.data]);
  const pick = (i: number) => {
    const h = hits[i];
    const srvHit = h.ref.kind === "ont" ? srv.data?.onts.find((x) => x.id === (h.ref as { id: number }).id) : undefined;
    onLocate(h.ref, srvHit);
    setOpen(false);
  };
  return (
    <div className="relative ml-auto w-full max-w-md">
      <FiSearch className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-slate-500" />
      <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === "Enter" && hits.length) pick(0); if (e.key === "Escape") setOpen(false); }}
        placeholder={t("topo.search")} aria-label={t("topo.search")}
        className="h-8 w-full rounded-md border border-rule bg-panel pr-8 pl-8 text-[12.5px] text-slate-100 outline-none placeholder:text-slate-500 focus:border-signal/50" />
      {q && <button type="button" onClick={() => { setQ(""); setOpen(false); }} aria-label={t("common.close")} className="absolute top-1/2 right-2 -translate-y-1/2 text-slate-500 hover:text-slate-200"><FiX /></button>}
      {open && q.trim() && (
        <div className="absolute right-0 z-30 mt-1 max-h-[26rem] w-full overflow-y-auto rounded-md border border-rule bg-[#070b14] py-1 shadow-2xl">
          {hits.length === 0 && <p className="px-3 py-2 text-[12.5px] text-slate-500">{srv.isFetching ? t("common.loading") : t("topo.noHits")}</p>}
          {hits.map((h, i) => (
            <button key={refKey(h.ref) + i} type="button" onClick={() => pick(i)}
              className="flex w-full items-start gap-2.5 px-3 py-1.5 text-left hover:bg-white/[0.04]">
              <span className={cn("mt-1.5 size-1.5 shrink-0 rounded-full", STATE_UI[h.state].dot)} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] text-slate-100">{h.label} <span className="data text-[11.5px] text-slate-500">{h.sub}</span></span>
                <span className="block truncate text-[11.5px] text-slate-500">{[t(`topo.kind.${h.ref.kind}` as Key), ...h.path].join("  →  ")}</span>
              </span>
              <span className="mt-0.5 inline-flex shrink-0 items-center gap-1 text-[11.5px] text-signal"><FiCrosshair /> {t("topo.locate")}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- detail panel ----------

function DetailPanel({ tree, sel, pinned, topo, now, onLocate, discStale }: {
  tree: SiteNode[]; sel: NodeRef | null; pinned: OntHit | null; topo: TopologyResponse; now: number; onLocate: (r: NodeRef) => void; discStale: boolean;
}) {
  const { t, locale } = useI18n();
  const n = (v: number) => fmtNum(locale, v);
  // Resolve the selection and its ancestry for the breadcrumb.
  let site: SiteNode | undefined, olt: OLTNode | undefined, pon: PONNode | undefined, ont: ONTNode | undefined, dev: DeviceNode | undefined;
  for (const s of tree) {
    if (sel?.kind === "site" && s.name === sel.site) site = s;
    if (sel?.kind === "device") { const d = s.devices.find((x) => x.id === sel.id); if (d) { site = s; dev = d; } }
    for (const o of s.olts) {
      if (sel?.kind === "olt" && o.id === sel.id) { site = s; olt = o; }
      for (const p of o.pons) {
        if ((sel?.kind === "pon" && p.id === sel.id) || (sel?.kind === "ont" && p.id === sel.ponId)) { site = s; olt = o; pon = p; }
        if (sel?.kind === "ont" && p.id === sel.ponId) ont = p.problems.find((x) => x.id === sel.id);
      }
    }
  }
  const crumbs: { label: string; ref?: NodeRef }[] = [{ label: "JALABYTE" }];
  if (site) crumbs.push({ label: site.name || t("topo.unassigned"), ref: { kind: "site", site: site.name } });
  if (olt) crumbs.push({ label: olt.name, ref: { kind: "olt", id: olt.id } });
  if (pon) crumbs.push({ label: `PON ${pon.path}`, ref: { kind: "pon", id: pon.id } });
  if (sel?.kind === "ont") crumbs.push({ label: `ONT #${ont?.ont_id ?? pinned?.ont_id ?? ""}` });
  if (dev) crumbs.push({ label: dev.name });

  const devName = (id: number) => topo.devices.find((d) => d.id === id)?.name ?? `#${id}`;
  const action = "inline-flex items-center gap-1.5 rounded-sm border border-rule px-2.5 py-1 text-[12px] text-slate-200 hover:border-signal/50 hover:text-signal";

  return (
    <aside className="rounded-lg border border-rule bg-panel xl:sticky xl:top-4">
      <nav aria-label="breadcrumb" className="flex flex-wrap items-center gap-1 border-b border-rule px-4 py-2.5 text-[12px]">
        {crumbs.map((c, i) => (
          <span key={i} className="inline-flex items-center gap-1">
            {i > 0 && <span className="text-slate-600">/</span>}
            {c.ref && i < crumbs.length - 1
              ? <button type="button" onClick={() => onLocate(c.ref!)} className="text-slate-400 hover:text-signal">{c.label}</button>
              : <span className={i === crumbs.length - 1 ? "font-medium text-slate-100" : "text-slate-400"}>{c.label}</span>}
          </span>
        ))}
      </nav>
      <div className="space-y-3 px-4 py-3 text-[13px]">
        {!sel && (
          <>
            <p className="text-slate-300">{t("topo.detail.pick")}</p>
            <Legend />
          </>
        )}

        {sel?.kind === "site" && site && (
          <>
            <Head title={site.name || t("topo.unassigned")} state={site.name ? site.state : undefined} />
            <KV k={t("topo.sum.olts")}>{site.olts.map((o) => <span key={o.id} className="mr-3 inline-flex items-center gap-1.5"><span className={cn("size-1.5 rounded-full", STATE_UI[o.state].dot)} />{o.name}</span>)}{!site.olts.length && "—"}</KV>
            <KV k={t("topo.sum.onts")}><Mono>{n(site.online)}/{n(site.total)}</Mono></KV>
            <KV k={t("topo.sum.devices")}><Mono>{n(site.devices.length)}</Mono></KV>
            {!site.name && <p className="text-[12px] text-slate-500">{t("topo.unassignedHint")}</p>}
          </>
        )}

        {sel?.kind === "olt" && olt && (
          <>
            <Head title={olt.name} sub={`${olt.ip} · ${[olt.vendor, olt.model].filter(Boolean).join(" ")}`} state={olt.state} />
            {olt.reasons.length > 0 && olt.state !== "healthy" && <p className="text-slate-300">{olt.reasons.map((r) => t(`olt.reason.${r}` as Key)).join(" · ")}</p>}
            <KV k={t("topo.detail.reach")}>
              <span className={olt.status === "unreachable" ? "text-signal-crit" : "text-slate-200"}>{t(olt.status === "unreachable" ? "topo.reach.down" : olt.status === "unknown" ? "topo.reach.unknown" : "topo.reach.up")}</span>
              {olt.lastPolledAt && <span className="text-slate-500"> · {t("topo.polled", { age: agoText(olt.lastPolledAt, now) })}</span>}
            </KV>
            <KV k={t("topo.sum.pons")}><Mono>{n(olt.pons.length)}</Mono></KV>
            <KV k={t("topo.sum.onts")}><Mono>{n(olt.online)}/{n(olt.total)}</Mono>{olt.total > olt.online && <span className="text-signal-crit"> · {t("topo.nOffline", { n: n(olt.total - olt.online) })}</span>}</KV>
            <KV k={t("topo.detail.temp")}><Mono>{olt.tempC === null ? "—" : `${olt.tempC.toFixed(0)} °C`}</Mono></KV>
            <KV k={t("topo.detail.alerts")}><Mono>{n(olt.alerts)}</Mono></KV>
            <div className="flex flex-wrap gap-2 pt-1">
              <Link className={action} to={`/olts/${olt.id}`}><FiExternalLink /> {t("topo.openOlt")}</Link>
              <Link className={action} to={`/netmap?focus=${encodeURIComponent(olt.mapKey)}`}><FiShare2 /> {t("topo.viewMap")}</Link>
              {olt.mapKey.startsWith("librenms:") && <Link className={action} to={`/graphs?device=${olt.mapKey.replace("librenms:", "")}`}>{t("lm.viewTraffic")}</Link>}
            </div>
          </>
        )}

        {sel?.kind === "pon" && pon && olt && (
          <>
            <Head title={`PON ${pon.path}`} sub={`${olt.name} · ${pon.native}`} state={pon.state} />
            <KV k={t("topo.sum.onts")}><Mono>{n(pon.total)}</Mono></KV>
            <KV k={t("topo.detail.online")}><Mono>{n(pon.online)}</Mono></KV>
            <KV k={t("topo.detail.offline")}><Mono className={pon.total > pon.online ? "text-signal-crit" : ""}>{n(pon.total - pon.online)}</Mono></KV>
            <KV k={t("topo.detail.weak")}><Mono className={pon.weak ? "text-signal-warn" : ""}>{n(pon.weak)}</Mono></KV>
            {pon.lastDownAt && pon.total > pon.online && <KV k={t("topo.detail.lastDrop")}><span title={fmtDateTime(locale, pon.lastDownAt)}>{agoText(pon.lastDownAt, now)}</span></KV>}
            <div className="flex flex-wrap gap-2 pt-1">
              <Link className={action} to={`/olts/${olt.id}?pon=${encodeURIComponent(pon.path)}`}><FiExternalLink /> {t("topo.openPon")}</Link>
              <Link className={action} to={`/onts?olt=${olt.id}&pon=${encodeURIComponent(pon.path)}`}>{t("topo.ontList")}</Link>
            </div>
          </>
        )}

        {sel?.kind === "ont" && (ont || pinned) && (
          <>
            <Head title={(ont?.description || pinned?.description) || (ont?.serial ?? pinned?.serial ?? "")} sub={`ONT #${ont?.ont_id ?? pinned?.ont_id} · ${ont?.serial ?? pinned?.serial}`}
              state={ont ? (ont.problem.sev === "crit" ? "critical" : "warn") : pinned?.run_state === "Online" ? "healthy" : "critical"} />
            {ont && (ont.problem.kind === "offline" || ont.problem.kind === "missing") && (
              <KV k={t("topo.detail.cause")}>{causeLabel(causeKind(ont.last_down_cause), t)} <span className="text-slate-500">· {agoText(ont.last_down_at, now)}</span></KV>
            )}
            {ont && ont.rx_power_dbm !== null && <KV k={t("topo.detail.rx")}><Mono>{fmtDbm(ont.rx_power_dbm)}</Mono></KV>}
            <div className="flex flex-wrap gap-2 pt-1">
              <Link className={action} to={`/onts/${sel.id}`}><FiExternalLink /> {t("topo.openOnt")}</Link>
            </div>
          </>
        )}

        {sel?.kind === "device" && dev && (
          <DeviceDetail dev={dev} topo={topo} devName={devName} action={action} discStale={discStale} now={now} />
        )}
      </div>
    </aside>
  );
}

function DeviceDetail({ dev, topo, devName, action, discStale, now }: { dev: DeviceNode; topo: TopologyResponse; devName: (id: number) => string; action: string; discStale: boolean; now: number }) {
  const { t, locale } = useI18n();
  const links = topo.links.filter((l) => l.a === dev.id || l.b === dev.id);
  const unres = topo.unresolved.filter((u) => u.from === dev.id);
  return (
    <>
      <Head title={dev.name} sub={`${dev.hostname} · ${[platformName(dev.os), dev.hardware].filter(Boolean).join(" · ")}`} state={dev.state} />
      <KV k={t("topo.detail.status")}>{t(`topo.dev.${dev.status}` as Key)}{dev.last_polled_at && <span className="text-slate-500"> · {t("topo.polled", { age: agoText(dev.last_polled_at, now) })}</span>}</KV>
      <KV k={t("topo.detail.site")}>{dev.site || "—"}{dev.site_source === "name" && <span className="text-slate-500"> · {t("topo.siteInferred")}</span>}{dev.site_source === "librenms" && <span className="text-slate-500"> · LibreNMS</span>}</KV>
      <div>
        <div className="eyebrow mb-1 text-[10px]">{t("topo.detail.links")} <span className="data text-slate-500">{fmtNum(locale, links.length)}</span></div>
        {links.length === 0 && <p className="text-[12px] text-slate-500">{t("topo.noLinks")}</p>}
        <ul className="space-y-1">
          {links.map((l) => (
            <li key={`${l.a}-${l.b}`} className="flex items-center gap-2 text-[12.5px]">
              <FiCornerDownRight className="size-3 shrink-0 text-slate-600" />
              <span className="truncate text-slate-200">{devName(l.a === dev.id ? l.b : l.a)}</span>
              <span className="ml-auto shrink-0"><ConfBadge c={l.confidence} /></span>
            </li>
          ))}
        </ul>
        {unres.length > 0 && <p className="mt-1.5 text-[12px] text-amber-200/90">{t("topo.nUnresolvedFrom", { n: fmtNum(locale, unres.length) })}</p>}
        {discStale && <p className="mt-1.5 text-[12px] text-amber-200">{t("topo.discStaleShort")}</p>}
      </div>
      <div className="flex flex-wrap gap-2 pt-1">
        <Link className={action} to={`/netmap?focus=${encodeURIComponent(dev.key)}`}><FiShare2 /> {t("topo.viewMap")}</Link>
        <Link className={action} to={`/graphs?device=${dev.key.replace("librenms:", "")}`}>{t("lm.viewTraffic")}</Link>
      </div>
    </>
  );
}

const Head = ({ title, sub, state }: { title: string; sub?: string; state?: NodeState }) => (
  <div>
    <div className="flex items-start gap-2">
      <h2 className="min-w-0 flex-1 text-[15px] leading-snug font-semibold break-words text-slate-100">{title}</h2>
      {state && <Badge state={state} />}
    </div>
    {sub && <p className="data mt-0.5 text-[12px] text-slate-500">{sub}</p>}
  </div>
);

const KV = ({ k, children }: { k: string; children: ReactNode }) => (
  <div className="grid grid-cols-[8.5rem_minmax(0,1fr)] gap-2 border-b border-rule/60 pb-1.5 text-[12.5px] last:border-b-0">
    <span className="text-slate-500">{k}</span><span className="min-w-0 text-slate-200">{children}</span>
  </div>
);

function Legend() {
  const { t } = useI18n();
  return (
    <div className="space-y-2 border-t border-rule/60 pt-3">
      <p className="eyebrow text-[10px]">{t("topo.legend")}</p>
      <div className="flex flex-wrap gap-1.5">
        {(["healthy", "warn", "degraded", "critical", "stale", "unknown"] as NodeState[]).map((s) => <Badge key={s} state={s} />)}
      </div>
      <p className="text-[12px] text-slate-500">{t("topo.legendHint")}</p>
    </div>
  );
}

// ---------- discovery ----------

function StaleNote() {
  const { t } = useI18n();
  return (
    <div className="flex items-center gap-2 rounded-md border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-[12.5px] text-amber-100">
      <FiClock className="text-signal-warn" /> {t("topo.discStale")}
    </div>
  );
}

function DiscoveryView({ topo, dc, disc, stale, now, onLocate, locale }: {
  topo: TopologyResponse; dc: ReturnType<typeof discoveryCounts>; disc: string | null; stale: boolean; now: number; onLocate: (r: NodeRef) => void; locale: string;
}) {
  const { t } = useI18n();
  const n = (v: number) => fmtNum(locale, v);
  const byId = new Map(topo.devices.map((d) => [d.id, d]));
  const ref = (id: number): NodeRef => { const d = byId.get(id); return d?.olt_id ? { kind: "olt", id: d.olt_id } : { kind: "device", id }; };
  const links = [...topo.links].sort((a, b) => ["confirmed", "discovered", "manual"].indexOf(a.confidence) - ["confirmed", "discovered", "manual"].indexOf(b.confidence));
  const linkN = new Map<number, number>();
  for (const l of topo.links) for (const id of [l.a, l.b]) linkN.set(id, (linkN.get(id) ?? 0) + 1);
  const devs = [...topo.devices].sort((a, b) => (a.site || "~").localeCompare(b.site || "~") || a.name.localeCompare(b.name));
  const DevName = ({ id }: { id: number }) => (
    <button type="button" onClick={() => onLocate(ref(id))} className="truncate text-left text-slate-100 hover:text-signal">{byId.get(id)?.name ?? `#${id}`}</button>
  );
  return (
    <div className="space-y-3">
      {stale && <StaleNote />}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg border border-rule bg-panel px-4 py-2.5 text-[12.5px] text-slate-400">
        <span>{t("topo.disc.source")} <span className="text-slate-100">LibreNMS</span>{disc && <> · {t("topo.disc.synced", { age: durText(now - Date.parse(disc)) })}</>}</span>
        <span><Mono className="text-slate-100">{n(dc.devices)}</Mono> {t("topo.disc.devices")}</span>
        <span><Mono className="text-slate-100">{n(dc.links)}</Mono> {t("topo.disc.links")}</span>
        {(["confirmed", "discovered", "manual"] as const).map((c) => <span key={c} className="inline-flex items-center gap-1.5"><ConfBadge c={c} /><Mono className="text-slate-100">{n(dc[c])}</Mono></span>)}
        <span className="text-amber-200/90"><Mono>{n(dc.unresolved)}</Mono> {t("topo.disc.unresolved")}</span>
        <Link to="/netmap" className="ml-auto inline-flex items-center gap-1 text-slate-300 hover:text-signal"><FiShare2 /> {t("topo.viewMap")}</Link>
      </div>
      <div className="grid items-start gap-3 xl:grid-cols-2">
        <section className="overflow-hidden rounded-lg border border-rule bg-panel">
          <h2 className="eyebrow border-b border-rule px-4 py-2">{t("topo.disc.knownDevices")}</h2>
          <table className="w-full table-fixed text-[12.5px]">
            <thead><tr className="border-b border-rule/70 text-left">
              {(["topo.col.device", "topo.col.platform", "topo.col.site", "topo.col.links", "topo.col.status"] as Key[]).map((k, i) => (
                <th key={k} className={cn("eyebrow px-3 py-1.5 text-[10px] font-medium", ["w-[34%]", "w-[26%]", "w-[18%]", "w-[10%]", "w-[12%]"][i])}>{t(k)}</th>))}
            </tr></thead>
            <tbody>
              {devs.map((d) => (
                <tr key={d.id} className="border-b border-rule/50 last:border-b-0 hover:bg-white/[0.02]">
                  <td className="px-3 py-1.5"><DevName id={d.id} /><div className="data text-[11px] text-slate-500">{d.hostname}{d.olt_id ? ` · ${t("topo.isOlt")}` : ""}</div></td>
                  <td className="truncate px-3 py-1.5 text-slate-300" title={d.hardware ?? ""}>{platformName(d.os) || "—"}<div className="truncate text-[11px] text-slate-500">{d.hardware}</div></td>
                  <td className="truncate px-3 py-1.5 text-slate-300">{d.site || <span className="text-slate-600">—</span>}{d.site_source === "name" && <div className="text-[11px] text-slate-500">{t("topo.inferred")}</div>}</td>
                  <td className="data px-3 py-1.5 text-slate-300">{linkN.get(d.id) ?? 0}</td>
                  <td className="px-3 py-1.5"><Badge state={d.status === "down" ? "degraded" : d.status === "unknown" ? "unknown" : "healthy"} quiet /> <span className="ml-1 text-slate-400">{t(`topo.dev.${d.status}` as Key)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <section className="overflow-hidden rounded-lg border border-rule bg-panel">
          <h2 className="eyebrow flex items-center gap-3 border-b border-rule px-4 py-2">{t("topo.disc.relationships")}
            <span className="ml-auto flex gap-3 normal-case tracking-normal">{(["confirmed", "discovered", "manual"] as const).map((c) => <span key={c} className="text-[11px] text-slate-500"><span className={CONF_UI[c].text}>{CONF_UI[c].glyph}</span> {t(`topo.conf.${c}.hint` as Key)}</span>)}</span>
          </h2>
          <table className="w-full table-fixed text-[12.5px]">
            <thead><tr className="border-b border-rule/70 text-left">
              {(["topo.col.local", "topo.col.remote", "topo.col.method", "topo.col.confidence"] as Key[]).map((k, i) => (
                <th key={k} className={cn("eyebrow px-3 py-1.5 text-[10px] font-medium", ["w-[31%]", "w-[31%]", "w-[16%]", "w-[22%]"][i])}>{t(k)}</th>))}
            </tr></thead>
            <tbody>
              {links.map((l) => (
                <tr key={`${l.a}-${l.b}`} className="border-b border-rule/50 last:border-b-0 hover:bg-white/[0.02]" title={l.interfaces.join(", ")}>
                  <td className="px-3 py-1.5"><DevName id={l.a} /></td>
                  <td className="px-3 py-1.5"><DevName id={l.b} /></td>
                  <td className="px-3 py-1.5 text-slate-300 uppercase">{[...l.protocols, ...(l.manual ? ["manual"] : [])].join(" + ") || "—"}
                    {l.interfaces.length > 1 && <div className="text-[11px] normal-case text-slate-500">{t("topo.viaN", { n: l.interfaces.length })}</div>}</td>
                  <td className="px-3 py-1.5"><ConfBadge c={l.confidence} /></td>
                </tr>
              ))}
              {links.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-500">{t("topo.noLinks")}</td></tr>}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}

// ---------- unresolved ----------

const REASON_ORDER: Unresolved[] = ["self", "ambiguous", "anonymous", "unmonitored"];

function UnresolvedView({ topo, stale, onLocate }: { topo: TopologyResponse; stale: boolean; onLocate: (r: NodeRef) => void }) {
  const { t, locale } = useI18n();
  const byId = new Map(topo.devices.map((d) => [d.id, d]));
  const [openR, setOpenR] = useState<Set<Unresolved>>(new Set(["self", "ambiguous", "anonymous"]));
  const groups = REASON_ORDER.map((r) => ({ r, rows: topo.unresolved.filter((u) => u.reason === r) })).filter((g) => g.rows.length);
  return (
    <div className="space-y-3">
      {stale && <StaleNote />}
      <p className="text-[12.5px] text-slate-400">{t("topo.unres.intro")}</p>
      {groups.length === 0 && <p className="rounded-lg border border-rule bg-panel px-4 py-8 text-center text-slate-400">{t("topo.unres.none")}</p>}
      {groups.map(({ r, rows }) => {
        const open = openR.has(r);
        // Many far ends per reporting device (an IX switch sees dozens of peers): group by reporter.
        const byFrom = new Map<number, TopoUnresolved[]>();
        for (const u of rows) byFrom.set(u.from, [...(byFrom.get(u.from) ?? []), u]);
        return (
          <section key={r} className="overflow-hidden rounded-lg border border-rule bg-panel">
            <button type="button" onClick={() => { const s = new Set(openR); if (s.has(r)) s.delete(r); else s.add(r); setOpenR(s); }}
              className="flex w-full items-start gap-3 px-4 py-2.5 text-left hover:bg-white/[0.02]">
              <span className="mt-0.5 text-slate-500">{open ? <FiChevronDown /> : <FiChevronRight />}</span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className={cn("text-[12px] font-semibold tracking-[0.12em] uppercase", r === "unmonitored" ? "text-slate-300" : "text-amber-200")}>{t(`topo.unres.${r}` as Key)}</span>
                  <Mono className="text-[12px] text-slate-500">{fmtNum(locale, rows.length)}</Mono>
                </span>
                <span className="mt-0.5 block text-[12.5px] text-slate-400">{t(`topo.unres.${r}.why` as Key)}</span>
                <span className="mt-0.5 block text-[12px] text-slate-500">{t("topo.unres.resolve")}: {t(`topo.unres.${r}.fix` as Key)}</span>
              </span>
            </button>
            {open && [...byFrom.entries()].map(([from, us]) => (
              <div key={from} className="border-t border-rule/70">
                <div className="flex items-center gap-2 px-4 pt-2 pb-1 text-[12px] text-slate-500">
                  {t("topo.unres.seenFrom")}
                  <button type="button" onClick={() => onLocate({ kind: "device", id: from })} className="text-slate-200 hover:text-signal">{byId.get(from)?.name ?? `#${from}`}</button>
                  <Mono className="text-slate-600">{byId.get(from)?.hostname}</Mono>
                  <Mono className="ml-auto">{fmtNum(locale, us.length)}</Mono>
                </div>
                <table className="w-full table-fixed text-[12.5px]">
                  <tbody>
                    {us.map((u) => (
                      <tr key={u.remote_name + u.from} className="border-t border-rule/40">
                        <td className="w-[36%] truncate px-4 py-1.5 text-slate-100" title={u.remote_name}>
                          {u.remote_name || <span className="text-slate-500 italic">{t("topo.unres.noName")}</span>}
                        </td>
                        <td className="w-[20%] truncate px-3 py-1.5 text-slate-400">{u.platform || "—"}</td>
                        <td className="w-[12%] px-3 py-1.5 text-slate-400 uppercase">{u.protocol || "—"}</td>
                        <td className="truncate px-3 py-1.5 text-slate-500" title={u.ports.join("\n")}>
                          <Mono>{u.ports[0] ?? "—"}</Mono>{u.count > 1 && <span className="ml-1.5 text-slate-600">{t("topo.unres.xRows", { n: u.count })}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </section>
        );
      })}
    </div>
  );
}
